import { createApp, h } from 'vue';
import MessageBoxModal from '../components/MessageBoxModal.vue';

const modals = new Map();
const normalizeId = (id) => String(id || '').replace(/^#/, '');
let parentApp = null;

export const registerModal = (id, modal) => {
	const normalizedId = normalizeId(id);
	if (!normalizedId) return () => {};

	modals.set(normalizedId, modal);

	return () => {
		if (modals.get(normalizedId) === modal) {
			modals.delete(normalizedId);
		}
	};
};

const getModal = (id) => modals.get(normalizeId(id));

const inheritParentAppContext = (messageBoxApp) => {
	if (!parentApp) {
		return;
	}

	Object.assign(messageBoxApp.config.globalProperties, parentApp.config.globalProperties);
	Object.assign(messageBoxApp._context.components, parentApp._context.components);
	Object.assign(messageBoxApp._context.directives, parentApp._context.directives);
	Object.assign(messageBoxApp._context.provides, parentApp._context.provides);
};

const showMessageBox = (message, options = {}, confirm = false) => new Promise((resolve) => {
	const host = document.createElement('div');
	host.className = 'bv-msgbox-host';
	document.body.appendChild(host);

	let app = null;
	let cleanupTimer = null;

	const cleanup = () => {
		if (cleanupTimer) { clearTimeout(cleanupTimer); cleanupTimer = null; }
		try { app?.unmount(); } catch (_) { /* noop */ }
		if (host.parentNode) host.parentNode.removeChild(host);
		app = null;
	};

	let settled = false;
	const onResolve = (value) => {
		if (settled) return;
		settled = true;
		resolve(value);
		cleanupTimer = setTimeout(cleanup, 300);
	};

	const propsForModal = {
		message,
		confirm,
		...options,
		onResolve,
	};

	app = createApp({
		name: 'BvMsgBoxHost',
		render: () => h(MessageBoxModal, propsForModal),
	});

	inheritParentAppContext(app);

	app.config.errorHandler = (err, _instance, info) => {
		// eslint-disable-next-line no-console
		console.error('[bv-msgbox] error', info, err);
		onResolve(null);
	};

	app.mount(host);
});

export const BootstrapVueModalPlugin = {
	install(app) {
		parentApp = app;

		const bvModal = {
			show(id) { getModal(id)?.show(); },
			hide(id) { getModal(id)?.hide(); },
			msgBoxOk(message, options) {
				return showMessageBox(message, options, false);
			},
			msgBoxConfirm(message, options) {
				return showMessageBox(message, options, true);
			},
		};

		app.config.globalProperties.$bvModal = bvModal;
		app.provide('bvModal', bvModal);
	},
};

export const msgBoxOk = (message, options) => showMessageBox(message, options, false);
export const msgBoxConfirm = (message, options) => showMessageBox(message, options, true);

export const useBvModal = () => ({
	msgBoxOk,
	msgBoxConfirm,
	show: (id) => getModal(id)?.show(),
	hide: (id) => getModal(id)?.hide(),
});
